import React from "react";
import { withRouter } from "react-router";
import withStyles from "@mui/styles/withStyles";
import IconButton from "@mui/material/IconButton";
import styles from "./styles";
import { NAVIGATION_PERMISSIONS } from "../../constants/navigationMapping";
import NavItem from "./NavItem";
import SideNavDrawer from "./SideNavDrawer";
import List from "@mui/material/List";
import { ArrowLineLeft, ArrowLineRight, Flask } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";
import { useEnabledExperimentFlags } from "../../hooks/useEnabledExperimentFlags";
import { useExperimentsManifest } from "../../hooks/useExperimentsManifest";
import { getEnabledExperimentsForAudience } from "../Experiments/experimentsManifest";

const SideNav = (props) => {
  const { classes, drawerOpen, handleDrawerToggle, mobile, navigate, user } = props;
  const { flags } = useEnabledExperimentFlags(user?.id);
  const { manifest } = useExperimentsManifest();

  const getFilteredNavMenuItems = () => {
    const { userPermissions } = props;

    if (!userPermissions) {
      return [];
    }

    let seenDashboard = false;

    // Convert object to array and filter
    const items = Object.values(NAVIGATION_PERMISSIONS).filter((item) => {
      // Check if item should be filtered based on user permissions
      const hasPermission = (perm) => {
        if (Array.isArray(perm)) {
          return perm.some((p) => userPermissions[p]);
        }
        return userPermissions[perm];
      };

      if (item.url !== "/dashboard") {
        return hasPermission(item.permission);
      }

      if (!seenDashboard && hasPermission(item.permission)) {
        seenDashboard = true;
        return true;
      }

      return false;
    });

    // Dynamic Experiments entry: derived from the lab manifest filtered by the
    // user's enabled `exp_iso_*` flags. Combines experiments from all audiences
    // the user has access to (admin users see both admin + clinician experiments).
    const audiences = [];
    if (userPermissions.view_admin_dashboard) audiences.push("admin");
    if (userPermissions.view_clinician_dashboard || userPermissions.view_admin_dashboard)
      audiences.push("clinician");

    const allEnabled = audiences.flatMap((aud) =>
      getEnabledExperimentsForAudience(manifest, flags, aud)
    );
    // Dedupe by id (in case overlaps) and sort
    const dedupedMap = new Map(allEnabled.map((e) => [e.id, e]));
    const deduped = [...dedupedMap.values()].sort((a, b) => a.title.localeCompare(b.title));

    if (deduped.length > 0) {
      const labBaseUrl = process.env.LAB_BASE_URL;
      const experimentsItem = {
        title: "Experiments",
        icon: <Flask size={20} color={"#D2B3EA"} />,
        iconSelected: <Flask size={20} color={"#FFFFFF"} />,
        // Opt-in to NavItem's compact sub-item layout (tighter padding + smaller
        // icon gutter) so this dynamic group looks correct without changing the
        // visuals of any existing sub-nav group.
        compactSubItems: true,
        pages: deduped.map((exp) => ({
          title: exp.title,
          url: `/experiments/${exp.id}`,
          // Only mark as external when labBaseUrl is configured; otherwise
          // NavItem falls back to internal navigation.
          ...(labBaseUrl ? { external: true, href: `${labBaseUrl}${exp.launchPath}` } : {}),
        })),
      };
      const messagesIndex = items.findIndex((item) => item.title === "Messages");
      if (messagesIndex !== -1) {
        items.splice(messagesIndex + 1, 0, experimentsItem);
      } else {
        items.push(experimentsItem);
      }
    }

    return items;
  };

  const pages = getFilteredNavMenuItems();

  const ArrowLine = drawerOpen ? ArrowLineLeft : ArrowLineRight;
  return (
    <SideNavDrawer open={drawerOpen} mobile={mobile} handleDrawerToggle={handleDrawerToggle}>
      <List disablePadding>
        {pages.map((page) => {
          return <NavItem {...props} navigate={navigate} page={page} key={page.title} />;
        })}
      </List>
      <div className={classes.toggleDrawerButtonContainer}>
        <Tooltip title={drawerOpen ? "Collapse" : "Expand"}>
          <IconButton
            color="inherit"
            aria-label={`${drawerOpen ? "collapse" : "expand"} drawer`}
            onClick={handleDrawerToggle}
          >
            <ArrowLine size={20} color={"#D2B3EA"} />
          </IconButton>
        </Tooltip>
      </div>
    </SideNavDrawer>
  );
};

export default withRouter(withStyles(styles)(SideNav));
